import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { defaultRefundLogFilters } from '../constants/refundLogRow';
import { defaultPaginationLight } from 'src/constants/main';
import { IPaginationLight, IResponse, LoadingStatus } from 'src/types/main';
import { IRefundLog, IRefundLogFilters, ITransactions } from 'src/view/screens/RefundLogScreen/screens/RefundLog/types/refundLog';
import client from 'src/services/client';
import logger from '~/services/logger';
import { dateFormatForRequest } from '~/constants/dateTime';
import { BaseStore } from '~/view/screens/SettingsScreen/store/BaseStore';

export class RefundLogStore extends BaseStore {
  constructor() {
    super();
    makeObservable(this);
  }

  @observable public exportLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public page: number = 1;

  @observable public pagination: IPaginationLight = defaultPaginationLight;
  @observable public transactions: IRefundLog[] = [];

  @observable public loading: boolean = false;
  @observable public isHasData: boolean = false;
  @observable public filters: IRefundLogFilters = defaultRefundLogFilters;

  private idGet: string = 'get';
  private idExport: string = 'export';

  @action public setFilters = (filters: Partial<IRefundLogFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.get();
  };

  @action public setPage = (page: number) => {
    this.page = page;
    this.get();
  };

  @action public fetch = () => {
    this.get();
  };

  @action public export = async () => {
    try {
      this.exportLoadingStatus = LoadingStatus.Loading;
      this.abort(this.idExport);
      await this.downloadFile('/v3/refund-transactions/log/export', this.getParams(this.idExport, this.getBody()));
      this.exportLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (this.isAborted(error)) return;
      logger.error({ error, place: this.idExport, showModal: true });
      this.exportLoadingStatus = LoadingStatus.Error;
    }
  };

  @action private get = async () => {
    try {
      this.abort(this.idGet);
      this.actionBeforeRequest();

      const response: IResponse<ITransactions> = await client.get('/v3/refund-transactions/log', this.getParams(this.idGet, this.getBody()));

      this.pagination = response.data?.pagination ?? defaultPaginationLight;
      this.transactions = response.data?.transactions ?? [];
      this.actionAfterRequest();
    } catch (error: any) {
      if (this.isAborted(error)) return;

      this.actionAfterRequest();

      logger.error({ error, place: this.idGet, showModal: true });
    }
  };

  @action public unMount = (): void => {
    this.page = 1;
    this.filters = defaultRefundLogFilters;

    this.loading = false;
    this.isHasData = false;

    this.transactions = [];
    this.pagination = defaultPaginationLight;
  };

  @action private getBody = () => {
    return {
      page: this.page,
      sort_by: this.filters.sortBy,
      sort_dir: this.filters.sortDir,
      filters: {
        actions: this.filters.actions,
        date_start: this.formatDate(this.filters.startDate),
        date_end: this.formatDate(this.filters.endDate),
      },
    };
  };

  @action public formatDate = (date: number) => {
    return moment(date * 1000).format(dateFormatForRequest);
  };

  @action private actionBeforeRequest = (): void => {
    this.loading = true;
    this.isHasData = false;
  };

  @action private actionAfterRequest = (): void => {
    this.loading = false;
    this.isHasData = this.loading === false && !!this.transactions.length;
  };
}

const refundLogStore = new RefundLogStore();

export default refundLogStore;
